/**
 * PRODUCTION SAFETY UTILITIES
 *
 * Centralized safety constants and validation functions for CaloBite.
 * Enforces medical-grade standards for calorie targets, allergens, and exercise safety.
 *
 * @version 1.0.0
 */

// =============================================================================
// SAFETY CONSTANTS
// =============================================================================

/**
 * Minimum safe daily calorie intake
 * Based on medical guidelines - below this risks metabolic slowdown
 */
export const MIN_SAFE_CALORIES = 1200;

/**
 * Maximum recommended daily calorie target
 * Above this may indicate calculation errors
 */
export const MAX_REASONABLE_CALORIES = 6000;

/**
 * Safety thresholds for nutrition values
 */
export const SAFETY_THRESHOLDS = {
    minCalories: 1200,
    maxCalories: 6000,
    maxSodiumMg: 3500,
    maxSugarG: 100,
    maxFatG: 200,
    minProteinG: 20,
    minWaterMl: 1500
};

/**
 * Poetic words that should be stripped from AI responses
 */
export const POETIC_WORDS = [
    'succulent', 'delightful', 'aromatic', 'fluffy', 'tender',
    'golden', 'kissed', 'nestled', 'symphony', 'drizzled',
    'heavenly', 'divine', 'exquisite', 'luscious', 'savory delight',
    'mouthwatering', 'decadent', 'sumptuous', 'delectable', 'tantalizing',
    'scrumptious', 'ambrosial', 'toothsome', 'palatable', 'appetizing',
    'zesty', 'velvety', 'silky', 'buttery smooth', 'perfectly seasoned'
];

// =============================================================================
// CALORIE VALIDATION
// =============================================================================

/**
 * Validate calorie target and return safe value
 * CRITICAL: Never returns a value below MIN_SAFE_CALORIES
 *
 * @param {number} target - Proposed calorie target
 * @param {Object} options - Optional configuration
 * @param {boolean} options.logWarnings - Whether to log warnings (default: true)
 * @returns {number} Safe calorie target (never below MIN_SAFE_CALORIES)
 */
export const validateCalorieTarget = (target, options = { logWarnings: true }) => {
    if (typeof target !== 'number' || isNaN(target)) {
        if (options.logWarnings) {
            console.warn('[ProductionSafety] Invalid calorie target, using minimum:', MIN_SAFE_CALORIES);
        }
        return MIN_SAFE_CALORIES;
    }

    if (target < MIN_SAFE_CALORIES) {
        if (options.logWarnings) {
            console.warn(
                `[ProductionSafety] Calorie target ${target} is below safe minimum. ` +
                `Adjusted to ${MIN_SAFE_CALORIES}`
            );
        }
        return MIN_SAFE_CALORIES;
    }

    if (target > MAX_REASONABLE_CALORIES) {
        if (options.logWarnings) {
            console.warn(
                `[ProductionSafety] Calorie target ${target} exceeds reasonable maximum. ` +
                `This may indicate a calculation error.`
            );
        }
        return MAX_REASONABLE_CALORIES;
    }

    return Math.round(target);
};

/**
 * Calculate safe calorie deficit
 * Ensures deficit doesn't push intake below MIN_SAFE_CALORIES
 *
 * @param {number} tdee - Total Daily Energy Expenditure
 * @param {number} deficitPercent - Desired deficit percentage (0-30)
 * @returns {Object} { targetCalories, actualDeficitPercent, capped }
 */
export const calculateSafeDeficit = (tdee, deficitPercent) => {
    const maxDeficit = Math.min(deficitPercent, 30); // Cap at 30%
    const rawTarget = tdee * (1 - maxDeficit / 100);

    if (rawTarget < MIN_SAFE_CALORIES) {
        return {
            targetCalories: MIN_SAFE_CALORIES,
            actualDeficitPercent: Math.round((1 - MIN_SAFE_CALORIES / tdee) * 100),
            capped: true
        };
    }

    return {
        targetCalories: Math.round(rawTarget),
        actualDeficitPercent: maxDeficit,
        capped: false
    };
};

// =============================================================================
// EXERCISE SAFETY
// =============================================================================

/**
 * Injury contraindications mapping
 * Maps injury types to exercises that should be avoided
 */
const INJURY_CONTRAINDICATIONS = {
    'knee_pain': ['running', 'jump rope', 'deep squat', 'lunges', 'burpees', 'box jump', 'plyometric'],
    'knee_injury': ['running', 'jump rope', 'deep squat', 'lunges', 'burpees', 'box jump', 'plyometric'],
    'shoulder_mobility': ['overhead press', 'pull-ups', 'shoulder press', 'bench press', 'dips', 'handstand'],
    'shoulder_injury': ['overhead press', 'pull-ups', 'shoulder press', 'bench press', 'dips', 'handstand'],
    'lower_back': ['deadlift', 'bent-over row', 'good morning', 'heavy squat', 'sit-ups', 'leg press'],
    'back_pain': ['deadlift', 'bent-over row', 'good morning', 'heavy squat', 'sit-ups', 'leg press'],
    'ankle_sprain': ['running', 'jump rope', 'box jumps', 'hiking', 'jumping jacks', 'lateral movement'],
    'wrist_injury': ['push-ups', 'plank', 'burpees', 'handstand', 'front squat'],
    'hip_injury': ['lunges', 'squats', 'running', 'cycling', 'leg press']
};

/**
 * Validate exercise is safe for user's injuries
 *
 * @param {string} exerciseName - Name of the exercise
 * @param {string[]} userInjuries - Array of user's injury types
 * @returns {Object} { isSafe, contraindication, suggestion }
 */
export const validateExerciseSafety = (exerciseName, userInjuries = []) => {
    if (!exerciseName || !userInjuries || userInjuries.length === 0) {
        return { isSafe: true, contraindication: null, suggestion: null };
    }

    const exerciseLower = exerciseName.toLowerCase();

    for (const injury of userInjuries) {
        const injuryKey = injury.toLowerCase().replace(/\s+/g, '_');
        const forbidden = INJURY_CONTRAINDICATIONS[injuryKey] || [];

        for (const bannedExercise of forbidden) {
            if (exerciseLower.includes(bannedExercise.toLowerCase())) {
                console.warn(
                    `[ProductionSafety] Exercise "${exerciseName}" contraindicated for ${injury}`
                );

                return {
                    isSafe: false,
                    contraindication: injury,
                    suggestion: getSafeAlternative(bannedExercise)
                };
            }
        }
    }

    return { isSafe: true, contraindication: null, suggestion: null };
};

/**
 * Get safe exercise alternative
 */
const getSafeAlternative = (unsafeExercise) => {
    const alternatives = {
        'running': 'swimming or stationary cycling',
        'jump rope': 'low-impact cardio or elliptical',
        'deep squat': 'wall sits or leg press with limited range',
        'lunges': 'step-ups with support',
        'overhead press': 'front raises or lateral raises',
        'pull-ups': 'lat pulldown with controlled motion',
        'deadlift': 'hip hinge with resistance band',
        'sit-ups': 'dead bug or bird dog exercises'
    };

    return alternatives[unsafeExercise] || 'consult a physical therapist for alternatives';
};

// =============================================================================
// ALLERGEN VALIDATION
// =============================================================================

/**
 * Common allergen mappings to trigger ingredients
 */
const ALLERGEN_TRIGGERS = {
    'gluten': ['wheat', 'barley', 'rye', 'bread', 'pasta', 'flour', 'couscous', 'bulgur'],
    'dairy': ['milk', 'cheese', 'yogurt', 'butter', 'cream', 'whey', 'casein', 'lactose'],
    'nuts': ['almond', 'walnut', 'cashew', 'peanut', 'pecan', 'hazelnut', 'pistachio', 'macadamia'],
    'tree_nuts': ['almond', 'walnut', 'cashew', 'pecan', 'hazelnut', 'pistachio', 'macadamia'],
    'peanuts': ['peanut', 'peanut butter', 'groundnut'],
    'soy': ['tofu', 'soy sauce', 'edamame', 'tempeh', 'miso', 'soybean'],
    'eggs': ['egg', 'mayonnaise', 'meringue', 'albumin'],
    'shellfish': ['shrimp', 'crab', 'lobster', 'oyster', 'mussel', 'clam', 'scallop'],
    'fish': ['salmon', 'tuna', 'cod', 'tilapia', 'fish sauce', 'anchovies'],
    'sesame': ['sesame', 'tahini', 'hummus']
};

/**
 * Check ingredients for allergen matches
 *
 * @param {Array} ingredients - Array of ingredient objects with 'name' property
 * @param {string[]} userAllergies - Array of user's allergies
 * @returns {Object[]} Array of detected allergens with ingredient info
 */
export const validateAllergenCheck = (ingredients = [], userAllergies = []) => {
    if (!ingredients || !userAllergies || userAllergies.length === 0) {
        return [];
    }

    const detectedAllergens = [];

    for (const allergen of userAllergies) {
        const allergenKey = allergen.toLowerCase().replace(/\s+/g, '_');
        const triggers = ALLERGEN_TRIGGERS[allergenKey] || [allergenKey];

        for (const ingredient of ingredients) {
            const ingredientName = (ingredient.name || ingredient).toString().toLowerCase();

            for (const trigger of triggers) {
                if (ingredientName.includes(trigger.toLowerCase())) {
                    detectedAllergens.push({
                        allergen: allergen,
                        trigger: trigger,
                        foundIn: ingredient.name || ingredient,
                        severity: 'high'
                    });
                }
            }
        }
    }

    if (detectedAllergens.length > 0) {
        console.warn('[ProductionSafety] Allergens detected:', detectedAllergens);
    }

    return detectedAllergens;
};

// =============================================================================
// POETIC LANGUAGE STRIPPING
// =============================================================================

/**
 * Check if text contains poetic/flowery language
 *
 * @param {string|Object} result - Text or object to check
 * @returns {boolean} True if poetic language detected
 */
export const isPoeticLanguage = (result) => {
    const text = typeof result === 'string' ? result : JSON.stringify(result);
    const lower = text.toLowerCase();

    return POETIC_WORDS.some(word => lower.includes(word.toLowerCase()));
};

/**
 * Strip poetic language from text
 *
 * @param {string} text - Text to sanitize
 * @returns {string} Sanitized text without poetic words
 */
export const stripPoeticLanguage = (text) => {
    if (!text || typeof text !== 'string') return text;

    let cleaned = text;

    for (const word of POETIC_WORDS) {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        cleaned = cleaned.replace(regex, '');
    }

    // Clean up extra spaces
    cleaned = cleaned.replace(/\s+/g, ' ').trim();

    return cleaned;
};

/**
 * Sanitize ingredient name to remove poetic descriptions
 *
 * @param {string} name - Ingredient name to sanitize
 * @returns {string} Clean, scientific ingredient name
 */
export const sanitizeIngredientName = (name) => {
    if (!name || typeof name !== 'string') return name;

    let cleaned = stripPoeticLanguage(name);

    // Remove common unnecessary adjectives
    const unnecessaryAdjectives = [
        'fresh', 'organic', 'homemade', 'artisanal', 'premium',
        'finest', 'hand-picked', 'locally-sourced', 'farm-fresh'
    ];

    for (const adj of unnecessaryAdjectives) {
        const regex = new RegExp(`\\b${adj}\\b`, 'gi');
        cleaned = cleaned.replace(regex, '');
    }

    return cleaned.replace(/\s+/g, ' ').trim();
};

// =============================================================================
// VALIDATION SUMMARY
// =============================================================================

/**
 * Run all safety validations and return summary
 *
 * @param {Object} params
 * @param {number} params.calorieTarget - Target calories
 * @param {Array} params.ingredients - Meal ingredients
 * @param {string[]} params.allergies - User allergies
 * @param {string} params.exerciseName - Exercise name
 * @param {string[]} params.injuries - User injuries
 * @returns {Object} Safety validation summary
 */
export const runSafetyChecks = ({
    calorieTarget,
    ingredients = [],
    allergies = [],
    exerciseName = null,
    injuries = []
}) => {
    const issues = [];

    // Calorie check
    const safeCalories = validateCalorieTarget(calorieTarget, { logWarnings: false });
    if (safeCalories !== calorieTarget) {
        issues.push({
            type: 'calorie_adjustment',
            original: calorieTarget,
            adjusted: safeCalories,
            message: `Calorie target adjusted from ${calorieTarget} to ${safeCalories}`
        });
    }

    // Allergen check
    const allergenWarnings = validateAllergenCheck(ingredients, allergies);
    if (allergenWarnings.length > 0) {
        issues.push({
            type: 'allergen_warning',
            count: allergenWarnings.length,
            details: allergenWarnings,
            message: `${allergenWarnings.length} allergen(s) detected in ingredients`
        });
    }

    // Exercise safety check
    if (exerciseName) {
        const exerciseCheck = validateExerciseSafety(exerciseName, injuries);
        if (!exerciseCheck.isSafe) {
            issues.push({
                type: 'exercise_contraindication',
                exercise: exerciseName,
                injury: exerciseCheck.contraindication,
                suggestion: exerciseCheck.suggestion,
                message: `Exercise "${exerciseName}" may not be safe for ${exerciseCheck.contraindication}`
            });
        }
    }

    return {
        isValid: issues.length === 0,
        issueCount: issues.length,
        issues,
        safeCalorieTarget: safeCalories
    };
};

export default {
    MIN_SAFE_CALORIES,
    MAX_REASONABLE_CALORIES,
    SAFETY_THRESHOLDS,
    POETIC_WORDS,
    validateCalorieTarget,
    calculateSafeDeficit,
    validateExerciseSafety,
    validateAllergenCheck,
    isPoeticLanguage,
    stripPoeticLanguage,
    sanitizeIngredientName,
    runSafetyChecks
};
